import React, {useState, useRef, useContext, useEffect, useMemo, useReducer} from 'react';
import {VendorContext} from '../../../context/VendorProvider';
import {UserContext} from '../../../context/UserProvider';
import * as XLSX from 'xlsx';
import {FaSpinner, FaUpload, FaGripVertical, FaArrowLeft} from 'react-icons/fa';
import {FiTrash2} from 'react-icons/fi';
import SuccessNotification from '../../../components/toast/SuccessNotification';
import ErrorNotification from '../../../components/toast/ErrorNotification';
import DeleteModal from '../../../components/modals/DeleteModal';
import ConfirmModal from '../../../components/modals/ConfirmModal';
import DLM_Vendor_Import_Fields_Ods from '../../../assets/excel/DLM_Vendor_Import_Fields.ods';
import DLM_Vendor_Import_Fields_Excel from '../../../assets/excel/DLM_Vendor_Import_Fields.xlsx';

const VendorImports = ({onSwitchToTable}) => {
  const {darkMode, websiteColumnVisibility} = useContext(UserContext);
  const {
    showSuccess,
    setShowSuccess,
    showError,
    setShowError,
    text,
    setText,
    importVendors
  } = useContext(VendorContext);

  const colorblindMode = localStorage.getItem('colorblindMode') || 'none';
  const CONTROL_BAR_HEIGHT = 64;
  const ADD_ROW_HEIGHT = 40;

  const [tableHeight, setTableHeight] = useState(0);
  const [fileData, setFileData] = useState([]);
  const [rawHeaders, setRawHeaders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showErrorTooltip, setShowErrorTooltip] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [importSuccess, setImportSuccess] = useState(false);

  const initialMappingState = {mappings: {}, version: 0};
  const mappingReducer = (state, action) => {
    switch (action.type) {
      case 'UPDATE_MAPPING': {
        const newMappings = {...state.mappings};
        const {header, value} = action.payload;
        if (value === '' || value == null) {
          delete newMappings[header];
        } else {
          newMappings[header] = String(value);
        }
        return {mappings: newMappings, version: state.version + 1};
      }
      case 'RESET_MAPPING':
        return {mappings: {}, version: state.version + 1};
      default:
        return state;
    }
  };
  const [mappingState, dispatchMapping] = useReducer(mappingReducer, initialMappingState);
  const columnMapping = mappingState.mappings;

  const [selectAllStatusYes, setSelectAllStatusYes] = useState(false);
  const [useCurrentStartDate, setUseCurrentStartDate] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // eslint-disable-next-line
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [unmappedColumns, setUnmappedColumns] = useState([]);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [isResetConfirm, setIsResetConfirm] = useState(false);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);

  const containerRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragColIndex = useRef(null);
  const templateDropdownRef = useRef(null);

  // adjust table height on resize
  useEffect(() => {
    const updateHeight = () => {
      const maxH = window.innerHeight - CONTROL_BAR_HEIGHT - ADD_ROW_HEIGHT;
      const des = window.innerHeight * 0.85 - CONTROL_BAR_HEIGHT - ADD_ROW_HEIGHT;
      setTableHeight(Math.min(des, maxH));
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  // close template dropdown on outside click
  useEffect(() => {
    const onClick = e => {
      if (
        templateDropdownRef.current &&
        !templateDropdownRef.current.contains(e.target)
      ) {
        setShowTemplateDropdown(false);
      }
    };
    if (showTemplateDropdown) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showTemplateDropdown]);

  // clean up invalid mapping entries
  useEffect(() => {
    const invalidEntries = Object.entries(columnMapping).filter(
      ([, idx]) => idx === '' || idx == null
    );
    if (invalidEntries.length > 0) {
      const newMappings = {...columnMapping};
      invalidEntries.forEach(([key]) => delete newMappings[key]);
      Object.keys(newMappings).forEach(key =>
        dispatchMapping({
          type: 'UPDATE_MAPPING',
          payload: {header: key, value: newMappings[key]}
        })
      );
    }
  }, [columnMapping]);

  // compute unmapped columns when showing confirmation, exclude "Legend"
  useEffect(() => {
    if (showConfirmModal) {
      const mappedIdxs = Object.values(columnMapping)
        .filter(idx => idx !== '' && idx != null)
        .map(idx => String(idx));
      const unmapped = rawHeaders
        .map((h, idx) => ({h, idx}))
        .filter(({h, idx}) => h !== 'Legend' && !mappedIdxs.includes(String(idx)))
        .map(({h}) => h);
      setUnmappedColumns(unmapped);
    }
  }, [showConfirmModal, columnMapping, rawHeaders, mappingState.version]);

  // auto-hide toasts
  useEffect(() => {
    if (showSuccess) {
      const t = setTimeout(() => setShowSuccess(false), 3000);
      return () => clearTimeout(t);
    }
  }, [showSuccess]);
  useEffect(() => {
    if (showError) {
      const t = setTimeout(() => setShowError(false), 10000);
      return () => clearTimeout(t);
    }
  }, [showError]);

  const rowsContainerHeight = tableHeight;

  // column metadata
  const columnOrder = [
    'vendorName',
    'status',
    'startDate',
    'endDate',
    'feedType',
    'vendorType',
    'vendorCode',
    'comments',
    'reciprocalAllowed'
  ];
  const required = ['vendorName', 'status', 'feedType', 'vendorType'];
  const metadata = ['vendorCode', 'reciprocalAllowed'];
  const override = ['startDate', 'endDate', 'comments'];
  const getGroup = name =>
    required.includes(name)
      ? 'required'
      : metadata.includes(name)
        ? 'metadata'
        : override.includes(name)
          ? 'override'
          : null;

  // legend colors config
  const iconConfigurations = useMemo(() => {
    const configs = {
      required: {color: '#22c55e', label: 'Required Field'},
      metadata: {color: '#a855f7', label: 'Metadata, optional'},
      override: {color: '#f59e0b', label: 'Override, optional'}
    };
    switch (colorblindMode) {
      case 'protanopia':
        configs.required.color = '#f97316';
        configs.metadata.color = '#db2777';
        configs.override.color = '#f472b6';
        break;
      case 'protanomaly':
        configs.required.color = '#eab308';
        configs.metadata.color = '#ea580c';
        configs.override.color = '#f97316';
        break;
      case 'deuteranopia':
        configs.required.color = '#22c55e';
        configs.metadata.color = '#d97706';
        configs.override.color = '#f59e0b';
        break;
      case 'tritanopia':
        configs.required.color = '#9333ea';
        configs.metadata.color = '#0d9489';
        configs.override.color = '#2dd4bf';
        break;
      case 'tritanomaly':
        configs.required.color = '#4f46e5';
        configs.metadata.color = '#9333ea';
        configs.override.color = '#a855f7';
        break;
      case 'achromatopsia':
        configs.required.color = '#6b7280';
        configs.metadata.color = '#4b5563';
        configs.override.color = '#9ca3af';
        break;
      default:
        break;
    }
    return configs;
  }, [colorblindMode]);

  // all columns + visibility
  const allColumns = useMemo(
    () =>
      columnOrder.map(name => {
        const match = websiteColumnVisibility.find(c => c.columnName === name);
        return {columnName: name, width: match?.width ?? 150};
      }),
    [websiteColumnVisibility]
  );
  const visibleColumns = useMemo(
    () => allColumns.filter(c => getGroup(c.columnName)),
    [allColumns]
  );

  // mapping headers: friendly names
  const mappingHeaders = useMemo(
    () =>
      visibleColumns.map(c => {
        const name = c.columnName
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/\b\w/g, l => l.toUpperCase());
        return name === 'Vendor Code'
          ? 'Vendor Code (limit 5 characters)'
          : name;
      }),
    [visibleColumns]
  );
  const tableHeaders = useMemo(
    () =>
      visibleColumns.map(c =>
        c.columnName
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/\b\w/g, l => l.toUpperCase())
      ),
    [visibleColumns]
  );

  const colorMap = {
    'Vendor Name': 'border-green-500',
    Status: 'border-green-500',
    'Feed Type': 'border-green-500',
    'Vendor Type': 'border-green-500',
    'Start Date': 'border-yellow-400',
    'End Date': 'border-yellow-400',
    Comments: 'border-yellow-400',
    'Vendor Code': 'border-purple-500',
    'Reciprocal Allowed': 'border-purple-500'
  };

  // required fields detection
  const criticalFields = useMemo(
    () =>
      mappingHeaders
        .map((h, i) =>
          ['Vendor Name', 'Feed Type', 'Vendor Type'].includes(h) ? i : -1
        )
        .filter(i => i >= 0),
    [mappingHeaders]
  );
  const hasEmptyRequiredFields = useMemo(() => {
    if (isPreviewMode) {
      const req = ['Vendor Name', 'Feed Type', 'Vendor Type'];
      return req.some(h => !columnMapping[h] || columnMapping[h] === '');
    } else {
      return fileData.some(row =>
        criticalFields.some(
          ci => !row[ci] || row[ci].toString().trim() === ''
        )
      );
    }
  }, [fileData, criticalFields, columnMapping, isPreviewMode]);
  useEffect(() => {
    setShowErrorTooltip(hasEmptyRequiredFields);
  }, [hasEmptyRequiredFields]);
  const missingDataFields = useMemo(() => {
    if (!submitAttempted || isPreviewMode) return [];
    const errs = [];
    fileData.forEach((row, ri) => {
      const miss = criticalFields
        .filter(ci => !row[ci] || row[ci].toString().trim() === '')
        .map(ci => mappingHeaders[ci]);
      if (miss.length > 0 && errs.length < 10)
        errs.push(`Row ${ri + 1}: ${miss.join(', ')}`);
    });
    return errs;
  }, [fileData, criticalFields, mappingHeaders, submitAttempted, isPreviewMode]);

  // date helpers
  const isValidDate = value => {
    const v = String(value).trim();
    if (!v) return false;
    const mmddyyyy = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
    return mmddyyyy.test(v);
  };
  const convertToDateString = value => {
    const v = String(value).trim();
    if (!v) return '';
    if (/^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/.test(v)) return v;
    const num = parseFloat(v);
    if (!isNaN(num) && num > 25569 && num < 2958465) {
      const date = new Date((num - 25569) * 86400000);
      const m = String(date.getUTCMonth() + 1).padStart(2, '0');
      const d = String(date.getUTCDate()).padStart(2, '0');
      const y = date.getUTCFullYear();
      return `${m}/${d}/${y}`;
    }
    return v;
  };
  const formatDateForInput = value => {
    if (!value || !isValidDate(value)) return '';
    const [m, d, y] = value.split('/');
    return `${y}-${m}-${d}`;
  };
  const formatDateFromInput = value => {
    if (!value) return '';
    const [y, m, d] = value.split('-');
    return `${m}/${d}/${y}`;
  };

  // boolean helper
  const isValidBoolean = value => {
    if (value == null) return false;
    const str = String(value).trim().toLowerCase();
    return (
      [
        'yes',
        'no',
        'true',
        'false',
        'active',
        'inactive',
        'n/a',
        'none',
        '?',
        'off',
        'on',
        '1',
        '0'
      ].includes(str) && !/\s/.test(value)
    );
  };

  // detect column types
  const columnValidity = useMemo(() => {
    const val = Array(rawHeaders.length)
      .fill()
      .map((_, idx) => ({
        idx,
        hasDate: false,
        hasBoolean: false,
        hasGeneral: false,
        hasData: false
      }));
    fileData.forEach(row => {
      row.forEach((cell, i) => {
        if (i >= val.length) return;
        if (cell != null && String(cell).trim() !== '') {
          val[i].hasData = true;
          if (isValidDate(cell)) {
            val[i].hasDate = true;
          } else if (isValidBoolean(cell)) {
            val[i].hasBoolean = true;
          } else {
            val[i].hasGeneral = true;
          }
        }
      });
    });
    return val.map(v => ({
      idx: v.idx,
      isValidDate: v.hasData && v.hasDate && !v.hasBoolean && !v.hasGeneral,
      isValidBoolean:
        v.hasData && v.hasBoolean && !v.hasDate && !v.hasGeneral,
      isGeneral: v.hasData && (v.hasGeneral || !v.hasDate),
      hasData: v.hasData
    }));
  }, [fileData, rawHeaders]);

  // FILE UPLOAD & PARSING
  const handleFileUpload = e => {
    dispatchMapping({type: 'RESET_MAPPING'});

    const f = e.target.files[0];
    if (!f) return;
    setIsLoading(true);
    setIsFileUploaded(true);
    setImportSuccess(false);
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const data = new Uint8Array(ev.target.result);
        const wb = XLSX.read(data, {type: 'array', raw: true});
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, {
          header: 1,
          raw: true,
          defval: ''
        });
        if (json.length === 0) {
          setText('No data found in the file');
          setShowError(true);
          setIsLoading(false);
          return;
        }
        let headerCounter = 1;
        const rawHdrs = json[0].map(h => {
          const str = String(h || '').trim();
          return str === '' ? `Header ${headerCounter}` : str;
        });
        if (rawHdrs.includes('Header 1')) headerCounter = 2;

        const out = json
          .slice(1)
          .map(rw => {
            const nr = Array(rawHdrs.length).fill('');
            rw.forEach((c, i) => {
              if (i < rawHdrs.length) {
                nr[i] = c != null ? convertToDateString(c) : '';
                if (
                  columnMapping['Vendor Code (limit 5 characters)'] &&
                  i ===
                  parseInt(
                    columnMapping['Vendor Code (limit 5 characters)'],
                    10
                  )
                ) {
                  nr[i] = String(nr[i]).substring(0, 5);
                }
              }
            });
            return nr;
          })
          .filter(row => row.some(cell => cell.trim() !== ''));

        const hasData = rawHdrs.map((_, idx) =>
          out.some(row => row[idx] && String(row[idx]).trim() !== '')
        );
        const filteredHeaders = rawHdrs.filter((_, idx) => hasData[idx]);
        const filteredData = out.map(row =>
          row.filter((_, idx) => hasData[idx])
        );

        setRawHeaders(filteredHeaders);
        setFileData(filteredData);

        setSelectAllStatusYes(false);
        setUseCurrentStartDate(false);
        setSubmitAttempted(false);
        setIsPreviewMode(true);
        setText('File uploaded, please map columns');
        setShowSuccess(true);
      } catch {
        setText('Error reading file');
        setShowError(true);
      } finally {
        setIsLoading(false);
      }
    };
    reader.readAsArrayBuffer(f);
  };

  const handleShowConfirmModal = () => setShowConfirmModal(true);

  // CONFIRM MAPPING => transform preview data
  const handleConfirmMapping = () => {
    const mapped = fileData.map((row) => {
      const nr = Array(mappingHeaders.length).fill('');
      mappingHeaders.forEach((h, idx) => {
        const rawIdx = columnMapping[h];
        if (rawIdx != null && rawIdx !== '') {
          const ci = parseInt(rawIdx, 10);
          if (!isNaN(ci) && ci >= 0 && ci < row.length) {
            const val = row[ci] || '';
            if (['Start Date', 'End Date'].includes(h)) {
              nr[idx] = val;
            } else if (h === 'Status') {
              const s = String(val).trim().toLowerCase();
              nr[idx] = ['yes', 'true', 'active'].includes(s)
                ? 'Active'
                : 'Inactive';
            } else if (h === 'Reciprocal Allowed') {
              const s = String(val).trim().toLowerCase();
              nr[idx] = ['yes', 'true', 'active'].includes(s) ? 'Yes' : 'No';
            } else if (h === 'Vendor Code (limit 5 characters)') {
              nr[idx] = String(val).substring(0, 5);
            } else {
              nr[idx] = val;
            }
          }
        }
      });
      return nr;
    });

    if (selectAllStatusYes) {
      const si = mappingHeaders.indexOf('Status');
      mapped.forEach(r => {
        if (si !== -1) r[si] = 'Active';
      });
    }
    if (useCurrentStartDate) {
      const sdi = mappingHeaders.indexOf('Start Date');
      mapped.forEach(r => {
        if (sdi !== -1) {
          r[sdi] = formatDateFromInput(selectedStartDate);
        }
      });
    }

    setFileData(mapped);
    setSubmitAttempted(false);
    setIsPreviewMode(false);
    setShowConfirmModal(false);
    setText('Columns mapped successfully');
    setShowSuccess(true);
  };

  // edit individual cell
  const handleCellEdit = (r, c, v) => {
    const h = mappingHeaders[c];
    const upd = [...fileData];
    if (['Start Date', 'End Date'].includes(h)) {
      const fmt = formatDateFromInput(v);
      if (v && !isValidDate(fmt)) {
        setText(`Invalid date for ${h}. Use MM/DD/YYYY.`);
        setShowError(true);
        return;
      }
      upd[r][c] = fmt;
    } else if (h === 'Vendor Code (limit 5 characters)') {
      upd[r][c] = String(v).substring(0, 5);
    } else {
      upd[r][c] = v;
    }
    setFileData(upd);
  };

  // drag-drop for column reorder
  const handleDragStart = (e, idx) => {
    dragColIndex.current = idx;
    e.dataTransfer.effectAllowed = 'move';
    const ghost = document.createElement('div');
    ghost.id = 'drag-ghost';
    ghost.style.position = 'absolute';
    ghost.style.top = '-9999px';
    ghost.style.left = '-9999px';
    ghost.style.padding = '8px 12px';
    ghost.style.background = darkMode ? '#444' : '#ddd';
    ghost.style.color = darkMode ? '#fff' : '#000';
    ghost.style.border = darkMode ? '1px solid #555' : '1px solid #bbb';
    ghost.style.borderRadius = '4px';
    ghost.style.whiteSpace = 'nowrap';
    ghost.innerText = tableHeaders[idx];
    document.body.appendChild(ghost);
    const rect = ghost.getBoundingClientRect();
    e.dataTransfer.setDragImage(ghost, rect.width / 2, rect.height / 2);
    e.currentTarget.classList.add('opacity-50');
  };
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (e, tgt) => {
    e.preventDefault();
    const src = dragColIndex.current;
    if (src == null || src === tgt) return;
    const reordered = fileData.map(row => {
      const tmp = [...row];
      const [x] = tmp.splice(src, 1);
      tmp.splice(tgt, 0, x);
      return tmp;
    });
    setFileData(reordered);
    dragColIndex.current = null;
    document
      .querySelectorAll('.drag-handle')
      .forEach(el => el.classList.remove('opacity-50'));
  };
  const handleDragEnd = (e) => {
    const g = document.getElementById('drag-ghost');
    if (g) g.remove();
    e.currentTarget.classList.remove('opacity-50');
  };

  // final save/import
  const handleSave = async () => {
    setIsLoading(true);
    try {
      const validRows = fileData.filter(row => {
        const hasReq = criticalFields.every(
          ci => row[ci] && row[ci].toString().trim() !== ''
        );
        const vcIdx = mappingHeaders.indexOf(
          'Vendor Code (limit 5 characters)'
        );
        const hasCode =
          vcIdx === -1 || (row[vcIdx] && row[vcIdx].toString().trim() !== '');
        const vtIdx = mappingHeaders.indexOf('Vendor Type');
        const hasType =
          vtIdx === -1 || (row[vtIdx] && row[vtIdx].toString().trim() !== '');
        return hasReq && hasCode && hasType;
      });
      if (validRows.length === 0) {
        setText(
          'No valid rows to import: required fields, Vendor Code, or Vendor Type missing'
        );
        setShowError(true);
        setIsLoading(false);
        return;
      }
      const csv = validRows
        .map(rw =>
          rw
            .map((c, i) => {
              if (
                mappingHeaders[i] === 'Vendor Code (limit 5 characters)'
              ) {
                return `"${String(c || '00000')
                  .replace(/"/g, '""')
                  .substring(0, 5)}"`;
              }
              if (mappingHeaders[i] === 'Vendor Type') {
                return `"${String(c || 'Gateway').replace(/"/g, '""')}"`;
              }
              return `"${String(c || '').replace(/"/g, '""')}"`;
            })
            .join('|')
        )
        .join('\n');
      await importVendors(csv);
      setFileData([]);
      setRawHeaders([]);
      setImportSuccess(true);
      setText('Import success');
      setShowSuccess(true);
    } catch (err) {
      console.error('Error in handleSave:', err);
      setText('Error importing data');
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddRow = () => {
    setFileData(prev =>
      prev.length === 0
        ? [Array(mappingHeaders.length).fill('')]
        : [...prev, Array(mappingHeaders.length).fill('')]
    );
    setIsFileUploaded(false);
  };

  const handleReset = () => {
    setIsResetConfirm(true);
    setShowDeleteModal(true);
  };
  const confirmReset = () => {
    setFileData([]);
    setRawHeaders([]);
    setIsFileUploaded(false);
    dispatchMapping({type: 'RESET_MAPPING'});
    setSelectAllStatusYes(false);
    setUseCurrentStartDate(false);
    setSubmitAttempted(false);
    setIsPreviewMode(false);
    setImportSuccess(false);
    setText('Table reset successfully');
    setShowSuccess(true);
    setIsResetConfirm(false);
    closeDeleteModal();
  };

  const handleDeleteRow = ri => {
    setRowToDelete(ri);
    setShowDeleteModal(true);
  };
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setRowToDelete(null);
    setIsResetConfirm(false);
  };
  const confirmDelete = () => {
    if (rowToDelete != null) {
      setFileData(prev => {
        const tmp = [...prev];
        tmp.splice(rowToDelete, 1);
        return tmp;
      });
      setText('Row deleted successfully');
      setShowSuccess(true);
    }
    closeDeleteModal();
  };
  const handleModalConfirm = () => {
    isResetConfirm ? confirmReset() : confirmDelete();
  };

  const handleTemplateDownload = (file, filename) => {
    const link = document.createElement('a');
    link.href = file;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowTemplateDropdown(false);
  };

  const handleSaveAttempt = () => {
    setSubmitAttempted(true);
    const misses = [];
    fileData.forEach((row, ri) => {
      const miss = criticalFields
        .filter(ci => !row[ci] || row[ci].toString().trim() === '')
        .map(ci => mappingHeaders[ci]);
      if (miss.length > 0 && misses.length < 10)
        misses.push(`Row ${ri + 1}: ${miss.join(', ')}`);
    });
    if (misses.length > 0) {
      setShowErrorTooltip(true);
      setText(`Missing required fields: ${misses.join('; ')}`);
      setShowError(true);
    } else {
      setShowErrorTooltip(false);
      handleSave();
    }
  };

  // AVAILABLE COLUMNS: exclude any raw column named "Legend"
  const availableColumns = useMemo(() => {
    const cols = rawHeaders
      .map((h, idx) => {
        if (h === 'Legend') return null;
        const validity = columnValidity[idx];
        if (!validity || !validity.hasData) return null;
        return {
          header: h,
          idx,
          available: true,
          isValidDate: validity.isValidDate,
          isValidBoolean: validity.isValidBoolean,
          isGeneral: validity.isGeneral
        };
      })
      .filter(Boolean);
    return cols;
  }, [rawHeaders, columnValidity]);

  const getValidColumnsForHeader = header => {
    const currentIdx =
      columnMapping[header] != null && columnMapping[header] !== ''
        ? parseInt(columnMapping[header], 10)
        : null;
    const used = Object.values(columnMapping).filter(
      i => i !== '' && parseInt(i, 10) !== currentIdx
    );
    return availableColumns.filter(col => {
      if (col.idx === currentIdx) return true;
      if (used.includes(String(col.idx))) return false;
      if (header === 'Start Date' || header === 'End Date') {
        return col.isValidDate;
      }
      if (header === 'Status' || header === 'Reciprocal Allowed') {
        return col.isValidBoolean;
      }
      return col.isGeneral && !col.isValidDate && !col.isValidBoolean;
    });
  };

  // pre-warm valid columns
  useEffect(() => {
    if (fileData.length > 0) {
      mappingHeaders.forEach(header => getValidColumnsForHeader(header));
    }
  }, [columnValidity, availableColumns, mappingHeaders, fileData, columnMapping]);

  const baseInputClasses = `w-full bg-transparent border ${
    darkMode ? 'border-stone-700' : 'border-gray-300'
  } rounded px-1 py-0.5 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
    darkMode ? 'text-gray-300 dark:bg-stone-800' : 'text-gray-900 bg-white'
  } hover:border-blue-500 appearance-none min-w-[200px]`;

  const buttonClasses = darkMode
    ? 'px-4 py-1 max-h-8 rounded bg-indigo-500 font-semibold text-white hover:bg-indigo-600'
    : 'px-4 py-1 max-h-8 rounded bg-blue-500 font-semibold text-white hover:bg-blue-600';

  return (
    <div
      ref={containerRef}
      className={`relative w-full ${
        darkMode ? 'bg-stone-800 text-gray-300' : 'bg-white text-gray-900'
      }`}
      style={{height: '85vh'}}
    >
      {/* Top control bar */}
      <div className="sticky top-0 z-40 h-[50px] flex items-center justify-between bg-gray-400 dark:bg-stone-800 px-4">
        <h1 className="text-xl text-white font-semibold">Vendor Imports</h1>
        <div className="flex items-center space-x-2">
          {!isPreviewMode && submitAttempted && missingDataFields.length > 0 && (
            <div className="relative inline-block">
              <button
                onClick={() => setShowErrorTooltip(prev => !prev)}
                className="px-4 py-1 font-semibold text-red-500 hover:text-red-400"
              >
                Missing fields ({missingDataFields.length})
              </button>
              {showErrorTooltip && (
                <div
                  className="absolute right-0 w-[350px] bg-white dark:bg-stone-700 border border-red-500 rounded-lg shadow-lg p-2 z-50">
                  <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-200">
                    {missingDataFields.map((f, i) => (
                      <li key={i}>{f}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          {!isPreviewMode && (
            <div className="relative inline-block group">
              <button className="px-4 py-1 max-h-8 font-semibold text-white hover:text-gray-300">
                Legend
              </button>
              <div
                className={`
                  absolute left-0 w-48
                  ${darkMode ? 'bg-stone-600 text-gray-300 border-stone-500' : 'bg-gray-500 text-white border-gray-400'}
                  border rounded-lg shadow-lg p-2
                  opacity-0 pointer-events-none
                  transition-opacity duration-200
                  group-hover:opacity-100 group-hover:pointer-events-auto
                `}
              >
                {Object.entries(iconConfigurations).map(([key, {color, label}]) => (
                  <div key={key} className="flex items-center p-1 space-x-2">
                    <svg className="w-4 h-4" viewBox="0 0 16 16">
                      <rect x="0" y="0" width="16" height="16" fill={color}/>
                    </svg>
                    <span className="text-sm">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {!isPreviewMode && (
            <button
              onClick={onSwitchToTable}
              className="px-4 py-1 max-h-8 rounded bg-gray-500 font-semibold text-white hover:bg-gray-600 flex items-center"
            >
              <FaArrowLeft className="mr-2"/>
              Back
            </button>
          )}
          {!isPreviewMode && (
            <button
              onClick={handleReset}
              className="px-4 py-1 max-h-8 rounded bg-gray-500 font-semibold text-white hover:bg-gray-600"
            >
              Reset
            </button>
          )}
          <div className="relative inline-block" ref={templateDropdownRef}>
            <button
              onClick={() => setShowTemplateDropdown(prev => !prev)}
              className="px-4 py-1 max-h-8 rounded bg-gray-500 font-semibold text-white hover:bg-gray-600"
            >
              Template
            </button>
            {showTemplateDropdown && (
              <div
                className={`
                  absolute left-0 w-48 top-[calc(100%_+_4px)]
                  ${darkMode ? 'bg-stone-600 text-gray-300 border-stone-500' : 'bg-gray-500 text-white border-gray-400'}
                  border rounded-lg shadow-lg z-50
                `}
              >
                <button
                  onClick={() =>
                    handleTemplateDownload(DLM_Vendor_Import_Fields_Excel, 'DLM_Vendor_Import_Fields.xlsx')
                  }
                  className="w-full text-left px-4 py-2 hover:bg-gray-600 dark:hover:bg-stone-500"
                >
                  Download .xlsx
                </button>
                <button
                  onClick={() =>
                    handleTemplateDownload(DLM_Vendor_Import_Fields_Ods, 'DLM_Vendor_Import_Fields.ods')
                  }
                  className="w-full text-left px-4 py-2 hover:bg-gray-600 dark:hover:bg-stone-500"
                >
                  Download .ods
                </button>
              </div>
            )}
          </div>
          <label
            className="px-4 py-1 max-h-8 rounded bg-gray-500 cursor-pointer hover:bg-gray-600 text-white font-semibold flex items-center">
            <FaUpload className="mr-2"/>
            Upload File
            <input
              type="file"
              accept=".csv, .xlsx, .ods"
              onChange={handleFileUpload}
              className="hidden"
              ref={fileInputRef}
            />
          </label>
          {isPreviewMode ? (
            <button onClick={handleShowConfirmModal} className={buttonClasses}>
              Confirm
            </button>
          ) : (
            <button
              onClick={handleSaveAttempt}
              disabled={isLoading || fileData.length === 0}
              className={`${buttonClasses} disabled:opacity-50 disabled:cursor-not-allowed flex items-center`}
            >
              {isLoading ? (
                <>
                  <FaSpinner className="animate-spin mr-2"/>
                  Importing...
                </>
              ) : (
                'Import'
              )}
            </button>
          )}
        </div>
      </div>

      {/* Table / Preview Area */}
      <div
        style={{
          height: `${rowsContainerHeight}px`,
          maxHeight: `calc(100vh - ${CONTROL_BAR_HEIGHT + ADD_ROW_HEIGHT}px)`,
          overflowY: 'auto',
          overflowX: 'auto'
        }}
      >
        {isPreviewMode ? (
          // Preview & mapping UI
          <div className={`h-full ${darkMode ? 'bg-stone-800' : 'bg-white'}`}>
            <div className="p-4">
              <div className="mb-4 flex items-center space-x-4" style={{minHeight: '40px'}}>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectAllStatusYes}
                    onChange={e => setSelectAllStatusYes(e.target.checked)}
                    className="mr-2"
                  />
                  <span className={darkMode ? 'text-gray-300' : 'text-gray-900'}>
                    Set all Status to Active
                  </span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={useCurrentStartDate}
                    onChange={e => setUseCurrentStartDate(e.target.checked)}
                    className="mr-2"
                  />
                  <span className={darkMode ? 'text-gray-300' : 'text-gray-900'}>
                    Set Start Dates
                  </span>
                  <div
                    className="ml-2"
                    style={{width: useCurrentStartDate ? '200px' : '0', transition: 'width 0.2s'}}
                  >
                    {useCurrentStartDate && (
                      <input
                        type="date"
                        value={selectedStartDate}
                        onChange={e => setSelectedStartDate(e.target.value)}
                        className={`w-full bg-transparent border ${
                          darkMode ? 'border-stone-700' : 'border-gray-300'
                        } rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          darkMode ? 'text-gray-300 dark:bg-stone-800' : 'text-gray-900 bg-white'
                        } hover:border-blue-500 appearance-none min-w-[200px] inline-block`}
                      />
                    )}
                  </div>
                </label>
              </div>
              <table className="w-full border-collapse">
                <thead>
                <tr className={`${darkMode ? 'bg-stone-600' : 'bg-gray-500'}`}>
                  <th
                    className={`p-2 text-left border ${darkMode ? 'border-stone-500' : 'border-gray-300'} ${darkMode ? 'text-gray-300' : 'text-white'}`}>
                    Target Field
                  </th>
                  <th
                    className={`p-2 text-left border ${darkMode ? 'border-stone-500' : 'border-gray-300'} ${darkMode ? 'text-gray-300' : 'text-white'} min-w-[200px]`}>
                    Source Column
                  </th>
                  <th
                    className={`p-2 text-left border ${darkMode ? 'border-stone-500' : 'border-gray-300'} ${darkMode ? 'text-gray-300' : 'text-white'}`}>
                    Sample Data
                  </th>
                </tr>
                </thead>
                <tbody>
                {mappingHeaders.map(header => {
                  const colIdx =
                    columnMapping[header] != null && columnMapping[header] !== ''
                      ? parseInt(columnMapping[header], 10)
                      : null;
                  const sampleData =
                    colIdx != null &&
                    fileData[0] &&
                    fileData[0][colIdx] != null &&
                    fileData[0][colIdx] !== ''
                      ? String(fileData[0][colIdx])
                      : 'N/A';
                  return (
                    <tr key={header} className={`${darkMode ? 'bg-stone-800' : 'bg-white'}`}>
                      <td
                        className={`p-2 border ${darkMode ? 'border-stone-500' : 'border-gray-300'} ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                        {['Vendor Name', 'Status', 'Feed Type', 'Vendor Type'].includes(header)
                          ? `${header} (Required)`
                          : header}
                      </td>
                      <td
                        className={`p-2 border ${darkMode ? 'border-stone-500' : 'border-gray-300'} ${darkMode ? 'text-gray-300' : 'text-gray-900'} min-w-[200px]`}>
                        <select
                          value={columnMapping[header] || ''}
                          onChange={e =>
                            dispatchMapping({
                              type: 'UPDATE_MAPPING',
                              payload: {header, value: e.target.value}
                            })
                          }
                          className={baseInputClasses}
                        >
                          <option value="">None</option>
                          {getValidColumnsForHeader(header).map(col => (
                            <option key={col.idx} value={col.idx}>
                              {col.header}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td
                        className={`p-2 border ${darkMode ? 'border-stone-500' : 'border-gray-300'} ${darkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                        {sampleData}
                      </td>
                    </tr>
                  );
                })}
                </tbody>
              </table>
            </div>
          </div>
        ) : fileData.length > 0 ? (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-stone-700 table-fixed">
            <thead className={`sticky top-0 z-30 ${darkMode ? 'bg-stone-600' : 'bg-gray-500'}`}>
            <tr>
              <th className="px-2 h-[40px] text-xs font-medium uppercase text-white truncate border-b-4"
                  style={{width: '50px'}}></th>
              <th className="px-2 h-[40px] text-xs font-medium uppercase text-white truncate border-b-4"
                  style={{width: '50px'}}>#
              </th>
              {tableHeaders.map((header, idx) => (
                <th
                  key={header}
                  onDragOver={handleDragOver}
                  onDrop={e => handleDrop(e, idx)}
                  className={`px-2 text-xs font-medium uppercase text-white truncate border-b-[8px] ${colorMap[header] || ''}`}
                  style={{width: `${visibleColumns[idx].width}px`}}
                >
                  <div className="flex items-center">
                    <span className="flex-1 whitespace-nowrap">{header}</span>
                    <span
                      className="drag-handle cursor-grab"
                      draggable
                      onDragStart={e => handleDragStart(e, idx)}
                      onDragEnd={handleDragEnd}
                    >
                        <FaGripVertical/>
                      </span>
                  </div>
                </th>
              ))}
            </tr>
            </thead>
            <tbody className={`${darkMode ? 'bg-stone-900' : 'bg-gray-100'}`}>
            {fileData.map((row, rowIndex) => (
              <tr key={rowIndex}>
                <td className="px-2 py-1 text-sm text-center box-border" style={{width: '50px'}}>
                  <button
                    onClick={() => handleDeleteRow(rowIndex)}
                    className="text-red-500 hover:text-red-700 focus:outline-none"
                    title="Delete Row"
                  >
                    <FiTrash2/>
                  </button>
                </td>
                <td className="px-2 py-1 text-sm text-gray-500 dark:text-gray-400 box-border" style={{width: '50px'}}>
                  {rowIndex + 1}
                </td>
                {row.map((cell, colIndex) => {
                  const header = mappingHeaders[colIndex];
                  if (header === 'Status') {
                    return (
                      <td key={colIndex} className="px-2 py-1 text-sm box-border"
                          style={{width: `${visibleColumns[colIndex].width}px`}}>
                        <select
                          value={cell || 'Inactive'}
                          onChange={e => handleCellEdit(rowIndex, colIndex, e.target.value)}
                          className={baseInputClasses}
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </select>
                      </td>
                    );
                  } else if (header === 'Reciprocal Allowed') {
                    return (
                      <td key={colIndex} className="px-2 py-1 text-sm box-border"
                          style={{width: `${visibleColumns[colIndex].width}px`}}>
                        <select
                          value={cell || 'No'}
                          onChange={e => handleCellEdit(rowIndex, colIndex, e.target.value)}
                          className={baseInputClasses}
                        >
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      </td>
                    );
                  } else if (['Start Date', 'End Date'].includes(header)) {
                    const formatted = formatDateForInput(cell);
                    return (
                      <td key={colIndex} className="px-2 py-1 text-sm box-border"
                          style={{width: `${visibleColumns[colIndex].width}px`}}>
                        <input
                          type="date"
                          value={formatted}
                          onChange={e => handleCellEdit(rowIndex, colIndex, e.target.value)}
                          className={baseInputClasses}
                        />
                      </td>
                    );
                  } else {
                    return (
                      <td key={colIndex} className="px-2 py-1 text-sm box-border"
                          style={{width: `${visibleColumns[colIndex].width}px`}}>
                        <input
                          type="text"
                          value={cell || ''}
                          onChange={e => handleCellEdit(rowIndex, colIndex, e.target.value)}
                          onFocus={e => e.target.select()}
                          className={baseInputClasses}
                        />
                      </td>
                    );
                  }
                })}
              </tr>
            ))}
            </tbody>
          </table>
        ) : importSuccess ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex items-center space-x-2">
              <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"/>
              </svg>
              <p className="text-green-500 font-semibold">Import success</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500 dark:text-gray-400">No data</p>
          </div>
        )}
      </div>

      {/* bottom add row */}
      <div className="sticky bottom-0 h-[48px] flex items-end justify-start px-4 bg-transparent">
        {!isPreviewMode && (
          <button onClick={handleAddRow} className="text-blue-500 hover:text-blue-700 underline">
            + Add Row
          </button>
        )}
      </div>

      {/* notifications and modals */}
      {showSuccess && (
        <SuccessNotification
          show={showSuccess}
          onClose={() => setShowSuccess(false)}
          text={text}
        />
      )}
      {showError && (
        <ErrorNotification
          show={showError}
          onClose={() => setShowError(false)}
          text={text}
        />
      )}
      {showDeleteModal && (
        <DeleteModal
          title="Are you sure?"
          onCancel={closeDeleteModal}
          onDelete={handleModalConfirm}
        />
      )}
      {showConfirmModal && (
        <ConfirmModal
          title="Confirmation"
          onCancel={() => setShowConfirmModal(false)}
          onConfirm={handleConfirmMapping}
        >
          <p>
            Once you confirm, you cannot edit mappings. To make changes to your column mappings,
            you will need to reset the file and begin the process again.
            {unmappedColumns.length > 0 && (
              <>
                {' The following columns are not mapped: '}
                <span style={{fontWeight: 'bold', color: 'red'}}>
                  {unmappedColumns.join(', ')}
                </span>
                .
              </>
            )}
          </p>
        </ConfirmModal>
      )}
    </div>
  );
};

export default VendorImports;
